import React, { useEffect, useState } from 'react';
import {
  hexToRgb,
  rgbDataURL,
  capitalizeOnlyFirstLetter,
} from 'corporate-utils';

import Markdown from '../../markdown/Markdown';
import { getStrapiMedia } from '../../../utils/media';

import {
  CardContainerStyled,
  CardImageContainerStyled,
  CardStyled,
  Category,
  CTA,
  Description,
  LinkWrapper,
  OverlayStyled,
  Title,
} from './Card.style';
import { NewsType } from '../../../models/auto-generated-strapi-types/types/NewsType';
import router from 'next/router';
import { WrapComponentToLink } from '../../wrap-component-to-link/WrapComponentToLink';
import Link from 'next/link';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import ImageWithFallback from '../../image-with-fallback/ImageWithFallback';

interface CardProps {
  data: {
    category?: string;
    title?: string;
    description?: string;
    button?: any;
    url?: any;
    image?: any;
    overlayColor?: string;
    overlayColorHover?: string;
    date?: string;
    startDate?: string;
    endDate?: string;
    coverTextColor?: string;
    isLast?: boolean;
    types?: NewsType[];
    forceHideDate?: boolean;
  };
  section: string;
}

const Card: React.FC<CardProps> = ({ data, section }: CardProps) => {
  const {
    category,
    title,
    description,
    button,
    url,
    image,
    overlayColor,
    overlayColorHover,
    date,
    startDate,
    endDate,
    coverTextColor,
    isLast,
    types,
    forceHideDate,
  } = data || {};
  const [, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  const handleClick = (e: any) => {
    e.preventDefault();
    router.push(
      { pathname: url ?? button?.link },
      { pathname: url ?? button?.link },
      { locale: router.locale, shallow: false }
    );
  };

  return (
    <LinkWrapper
      isLast={isLast}
      onClick={(e: any) => {
        handleClick(e);
      }}
    >
      <CardContainerStyled
        overlay={overlayColor}
        overlayHover={overlayColorHover}
      >
        <>
          <CardImageContainerStyled>
            {image && image.data && image.data.attributes ? (
              <ImageWithFallback
                layout="fill"
                objectFit="cover"
                placeholder="blur"
                blurDataURL={
                  overlayColor
                    ? rgbDataURL(
                      hexToRgb(overlayColor)?.[0],
                      hexToRgb(overlayColor)?.[1],
                      hexToRgb(overlayColor)?.[2]
                    )
                    : rgbDataURL(
                      hexToRgb('ffffff')?.[0],
                      hexToRgb('ffffff')?.[1],
                      hexToRgb('ffffff')?.[2]
                    )
                }
                src={getStrapiMedia(image?.data?.attributes?.url ?? '')}
                alt={image?.data?.attributes?.alternativeText || ''}
                fallbackSrc={DEFAULT_IMAGE}
              />
            ) : (
              image &&
              typeof image === 'string' && (
                <ImageWithFallback
                  layout="fill"
                  objectFit="cover"
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image)}
                  alt={title ? title : ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )
            )}
          </CardImageContainerStyled>
          <OverlayStyled></OverlayStyled>
          <CardStyled
            overlay={overlayColor}
            section={section}
            className={section}
            color={coverTextColor}
            isLast={isLast}
          >
            <Category section={section}>
              {category && capitalizeOnlyFirstLetter(category)}
              {category &&
              ((types && types.length > 0) ||
                (startDate && !forceHideDate) ||
                (date && !forceHideDate))
                ? ' | '
                : ''}
              {types && types.length > 0
                ? types.map((type) => type?.attributes?.type).join(' | ')
                : ''}
              {startDate && !forceHideDate
                ? startDate + ' - ' + endDate
                : date && !forceHideDate
                  ? date
                  : ''}
            </Category>
            {title && (
              <Title section={section}>
                <WrapComponentToLink href={url}>
                  <span
                    onClick={(e: any) => {
                      e.preventDefault();
                    }}
                  >
                    <Markdown>{title}</Markdown>
                  </span>
                </WrapComponentToLink>
              </Title>
            )}
            {description && (
              <Description section={section}>{description}</Description>
            )}
            {button && button.text && button.link && (
              <LinkWrapper isLast={isLast}>
                <Link href={button.link} passHref prefetch={false}>
                  <CTA
                    section={section}
                    color={coverTextColor}
                    isLast={isLast}
                    href={button.link}
                    onClick={(e: any) => {
                      e.preventDefault();
                      router.push(
                        { pathname: button.link },
                        { pathname: button.link },
                        { locale: router.locale, shallow: false }
                      );
                    }}
                  >
                    {button.text}
                  </CTA>
                </Link>
              </LinkWrapper>
            )}
          </CardStyled>
        </>
      </CardContainerStyled>
    </LinkWrapper>
  );
};

export default Card;
